import { JsonPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
  ProjectChangeStageEvent,
  ProjectChangeStageEventType
} from '@modules/projects/shared/components/project-lost-stage-modal/project-lost-stage-modal.component';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToolsOptions } from './project-dashboard-header.const';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { SkeletonItemComponent } from '@shared/components/skeleton-item/skeleton-item.component';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { privateFile } from '@shared/helpers/private-file.helper';
import { downloadFile } from '@shared/helpers/download-file.helper';
import { SnackBarService } from '@core/services/snackbar.service';
import { Router } from '@angular/router';
import { getDashboardPathForTool } from '../../project-dashboard-navigation.const';
import { NavigateService } from '@shared/services/navigate.service';
import { ProjectDashboardHeaderResponsibleComponent } from './components/project-dashboard-header-responsible/project-dashboard-header-responsible.component';

export interface DashboardToolOption {
  key: string;
  label: string;
  icon?: string;
}

@Component({
  selector: 'project-dashboard-header',
  standalone: true,
  templateUrl: './project-dashboard-header.component.html',
  styleUrls: ['./project-dashboard-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AngularSvgIconModule,
    MatMenuModule,
    NgClass,
    TranslateModule,
    SkeletonItemComponent,
    JsonPipe,
    ProjectDashboardHeaderResponsibleComponent
  ]
})
export class ProjectDashboardHeaderComponent {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectApiService = inject(ProjectAPIService);
  private readonly s = inject(SnackBarService);
  private readonly t = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly n = inject(NavigateService);

  ProjectStage = ProjectStage;
  toolsMenuItems = ToolsOptions;

  project = input<Project | null>(null);

  toolDragStarted = output<DashboardToolOption>();
  toolDragEnded = output<void>();
  stageChanged = output<ProjectStage>();
  projectUpdated = output<Project>();

  protected projectStages = Object.keys(ProjectStage);
  protected toolsDropdownOpen = false;
  private dragImageAnchor: HTMLElement | null = null;
  @ViewChild('menuTrigger') private statusMenuTrigger?: MatMenuTrigger;
  projectController = new ProjectController(this.project());

  protected stageChanging = signal(false);
  protected projectStage = computed(() => this.project()?.stage);

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const clickedInsideHeader = !!target && this.elementRef.nativeElement.contains(target);
    const clickedInsideStatusMenu = !!target?.closest('.mat-menu-panel');

    if (!clickedInsideHeader && !clickedInsideStatusMenu) {
      this.toolsDropdownOpen = false;
      this.statusMenuTrigger?.closeMenu();
    }
  }

  protected toggleToolsDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.statusMenuTrigger?.closeMenu();
    this.toolsDropdownOpen = !this.toolsDropdownOpen;
  }

  protected onStatusMenuOpened(): void {
    this.toolsDropdownOpen = false;
  }

  protected onToolDragStart(event: DragEvent, item: DashboardToolOption): void {
    if (!event.dataTransfer) return;

    event.dataTransfer.setData('text/plain', item.key);
    event.dataTransfer.setData('application/x-intilio-tool', item.key);
    event.dataTransfer.effectAllowed = 'copy';
    this.dragImageAnchor = this.createHiddenDragImageAnchor();
    event.dataTransfer.setDragImage(this.dragImageAnchor, 0, 0);
    this.toolDragStarted.emit(item);
  }

  protected onToolDragEnd(): void {
    this.cleanupDragImageAnchor();
    this.toolDragEnded.emit();
  }

  protected onToolSelected(item: DashboardToolOption): void {
    this.toolsDropdownOpen = false;
    const projectId = this.project()?.id;
    if (!projectId) return;

    const routePath = getDashboardPathForTool(item.key);
    this.router.navigate(['/panel/projects/dashboard', projectId, routePath], {
      queryParams: { tool: item.key },
      queryParamsHandling: 'merge'
    });
  }

  private createHiddenDragImageAnchor(): HTMLElement {
    const anchor = document.createElement('div');
    anchor.style.position = 'fixed';
    anchor.style.top = '-9999px';
    anchor.style.left = '-9999px';
    anchor.style.width = '1px';
    anchor.style.height = '1px';
    anchor.style.opacity = '0';
    anchor.style.pointerEvents = 'none';
    document.body.appendChild(anchor);
    return anchor;
  }

  private cleanupDragImageAnchor(): void {
    this.dragImageAnchor?.remove();
    this.dragImageAnchor = null;
  }

  protected onStageChange(stage: ProjectStage): void {
    const project = this.project();
    if (!project || this.stageChanging() || this.project().stage === stage) return;

    const ctrl = new ProjectController(project);
    this.stageChanging.set(true);
    ctrl
      .setStage(project, stage, project?.basicDataBox?.termStart, project?.basicDataBox?.termEnd)
      .pipe(
        tap(({ type, project }: ProjectChangeStageEvent) => {
          if (type !== ProjectChangeStageEventType.CHANGED) return;
          this.stageChanged.emit(project.stage);
        }),
        finalize(() => this.stageChanging.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected goBackToProjectsList(): void {
    const listUrl = this.n.latestProjectDashboardBackUrl || this.n.latestProjectsListUrl || null;

    if (listUrl) {
      void this.router.navigateByUrl(listUrl);
      return;
    }

    this.n.go('projects-list');
  }

  protected edit(): void {
    this.projectController.edit(this.project());
  }

  protected downloadSpec(): void {
    this.projectApiService
      .getFileBlob(privateFile(this.project().specification.file), false)
      .pipe(
        tap((res) => downloadFile(res, this.project().specification.name)),
        catchError(() => {
          this.s.error(this.t.instant('ProjectSummary.RightSection.errorDownloadSpecification'));
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected remove(): void {
    const ctrl = new ProjectController(this.project());
    ctrl.remove(this.project());
  }
}
